import React, { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import styled from 'styled-components';

import ReactToPrint from 'react-to-print';

import moment from 'moment';

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Tab,
  Tabs,
  Popover,
  OverlayTrigger,
  Form,
  InputGroup,
  Spinner,
} from 'react-bootstrap';

import {
  faMap,
  faQuestionCircle,
  faLocationArrow,
  faArrowUp,
  faArrowDown,
  faEraser,
  faList,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import classnames from 'classnames';
import numeral from 'numeral';
import Paginator from '../Paginator';
import Indicadores from './Indicadores';

import {
  prepareDataDA,
  prepareDataDAT,
  prepareDataDashDpA,
  prepareTotaisAnuais,
  getRiscoEscala,
} from '../../utils';

// Mesmas cores de --vft-accent e --vft-accent-partial (o nivo não lê variáveis CSS)
const COR_DESMATAMENTO = '#9b1c1c';
const COR_ANO_PARCIAL = '#e8bcbc';

// A partir desse percentual de famílias em risco alto, o selo da tabela fica cheio
const PERCENTUAL_RISCO_FORTE = 30;

function Dashboard() {
  const [tab, setTab] = useState('desmatamento_anual_total');
  const [countProducers, setCountProducers] = useState('...');
  const [countAssociacoes, setCountAssociacoes] = useState('...');
  const [countMunicipios, setCountMunicipios] = useState('...');
  const [countAps, setCountAps] = useState('...');
  const [listApsCat, setListApsCat] = useState([]);
  const [listMuns, setListMuns] = useState([]);
  const [riscoXfamiliasTable, setRiscoXfamiliasTable] = useState(null);
  const [mainTableData, setMainTableData] = useState([]);
  const [totaisAnuais, setTotaisAnuais] = useState(null);
  const [dataStatus, setDataStatus] = useState(null);

  const [order, setOrder] = useState(
    sessionStorage.getItem('currentOrderMain')
      ? sessionStorage.getItem('currentOrderMain')
      : '',
  );
  const [direction, setDirection] = useState(
    sessionStorage.getItem('currentDirectionMain')
      ? sessionStorage.getItem('currentDirectionMain')
      : 'desc',
  );

  let history = useHistory();

  useEffect(() => {
    async function fetchMainTable() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/main?order=${order}&direction=${direction}`,
      );
      setMainTableData(data);
    }

    async function fetchBigNumbers() {
      const {
        data: { producers, associacoes, municipios, aps, riscoXfamilias },
      } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/big_numbers`,
      );
      setCountProducers(producers);
      setCountAssociacoes(associacoes);
      setCountMunicipios(municipios.total);
      setCountAps(aps.total);
      setListApsCat(aps.categorias);
      setListMuns(municipios.list);
      setRiscoXfamiliasTable(riscoXfamilias);
    }

    fetchMainTable();
    fetchBigNumbers();
  }, [order, direction]);

  useEffect(() => {
    async function fetchTotaisAnuais() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=total`,
      );
      setTotaisAnuais(prepareTotaisAnuais(data));
    }

    async function fetchDataStatus() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/status`,
      );
      setDataStatus(data);
    }

    fetchTotaisAnuais();
    fetchDataStatus();
  }, []);

  const maiorDesmTresAnos = Math.max(
    1,
    ...mainTableData.map(({ desm_last_three_years }) => parseFloat(desm_last_three_years) || 0),
  );

  const handleChangeOrder = (field) => {
    let newDirection;

    if (field === order) {
      newDirection = direction === 'asc' ? 'desc' : 'asc';
      setDirection(newDirection);
    } else {
      newDirection = field === '' ? 'desc' : 'asc';
      setDirection(newDirection);
      setOrder(field);
    }

    sessionStorage.setItem('currentOrderMain', field);
    sessionStorage.setItem('currentDirectionMain', newDirection);
  };

  const popover = (
    <StyledPopover id="popover-basic">
      <Popover.Title as="h3">Por categoria fundiária</Popover.Title>
      <Popover.Content>
        <Table striped>
          <thead>
            <tr>
              <th>Categoria</th>
              <th>Num. A. Protegidas</th>
              <th>Num. Fornecedores</th>
            </tr>
          </thead>
          <tbody>
            {listApsCat.map(({ categoria, totalAPs, totalProds }) => (
              <tr key={categoria}>
                <td>{categoria}</td>
                <StyledTD>{totalAPs}</StyledTD>
                <StyledTD>{totalProds}</StyledTD>
              </tr>
            ))}
          </tbody>
        </Table>
      </Popover.Content>
    </StyledPopover>
  );

  const popoverMuns = (
    <StyledPopover id="popover-basic">
      <Popover.Title as="h3">Fornecedores por município</Popover.Title>
      <Popover.Content>
        <Table striped>
          <thead>
            <tr>
              <th>Município</th>
              <th>Num. Fornecedores</th>
            </tr>
          </thead>
          <tbody>
            {listMuns.map(({ nome_mun, total }) => (
              <tr key={nome_mun}>
                <td>{nome_mun}</td>
                <StyledTD>{total}</StyledTD>
              </tr>
            ))}
          </tbody>
        </Table>
      </Popover.Content>
    </StyledPopover>
  );

  const popoverRisco = (
    <StyledPopover id="popover-basic">
      <Popover.Title as="h3">Risco</Popover.Title>
      <Popover.Content>
        <Table striped>
          <thead>
            <tr>
              <th>Risco</th>
              <th>Num. Famílias</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ALTO</td>
              <StyledTD>
                {riscoXfamiliasTable ? riscoXfamiliasTable['alto'] : '...'}
              </StyledTD>
            </tr>
            <tr>
              <td>BAIXO</td>
              <StyledTD>
                {riscoXfamiliasTable ? riscoXfamiliasTable['baixo'] : '...'}
              </StyledTD>
            </tr>
          </tbody>
        </Table>
      </Popover.Content>
    </StyledPopover>
  );

  const popoverChart1 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Desmatamento total anual na “área de influência” da cadeia de
        fornecimento da Veja por Associação (hectares/ano)
      </Popover.Content>
    </Popover>
  );
  const popoverChart2 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Desmatamento anual por associação vinculada a cadeia de forneciento da
        Veja (hectares/ano).
        <br />
        <b>Pode haver contagem dupla de desmatamento</b>
      </Popover.Content>
    </Popover>
  );
  const popoverChart3 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Média de desmatamento por família fornecedora da Veja (hectares/família)
      </Popover.Content>
    </Popover>
  );
  const popoverChart4 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Desmatamento anual nos municipios de atuação da Veja (hectares/ano)
      </Popover.Content>
    </Popover>
  );
  const popoverChart5 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Desmatamento anual nas categorias fundiárias de atuação da Veja
        (hectares/ano)
      </Popover.Content>
    </Popover>
  );

  const popoverChart6 = (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Sobre este gráfico</Popover.Title>
      <Popover.Content>
        Desmatamento anual por Microrregião de "influência" da Veja
        (hectares/ano)
      </Popover.Content>
    </Popover>
  );

  return (
    <StyledContainer>
      <Cabecalho>
        <div>
          <Sobretitulo>Vert Fair Trade · Painel</Sobretitulo>
          <h1>Risco de desmatamento na cadeia da borracha</h1>
        </div>
        <Acoes>
          <Button variant="dark" onClick={() => history.push('/home')}>
            <FontAwesomeIcon icon={faMap} /> Mapa interativo
          </Button>
        </Acoes>
      </Cabecalho>

      <Indicadores
        totais={totaisAnuais}
        risco={riscoXfamiliasTable}
        status={dataStatus}
      />

      <Contagens>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="bottom"
          overlay={popoverRisco}
        >
          <span tabIndex={0}><strong>{countProducers}</strong> famílias</span>
        </OverlayTrigger>
        <span><strong>{countAssociacoes}</strong> associações e cooperativas</span>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="bottom"
          overlay={popoverMuns}
        >
          <span tabIndex={0}><strong>{countMunicipios}</strong> municípios</span>
        </OverlayTrigger>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="bottom"
          overlay={popover}
        >
          <span tabIndex={0}><strong>{countAps}</strong> áreas protegidas</span>
        </OverlayTrigger>
      </Contagens>

      <TopMargedCard>
        <Card.Body>
          <TituloCartao>
            <Card.Title>Desmatamento anual nas áreas de influência</Card.Title>
            <span>hectares por ano · PRODES, e DETER no ano corrente</span>
          </TituloCartao>
          <Tabs variant="pills" activeKey={tab} onSelect={(k) => setTab(k)}>
            <Tab
              eventKey="desmatamento_anual_total"
              title={
                <TabTitle>
                  Desm. anual total
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart1}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDAT
                  totais={totaisAnuais}
                  ultimoDeter={dataStatus && dataStatus.last_deter}
                />
              </TabContainer>
            </Tab>
            <Tab
              eventKey="desmatamento_anual_assoc_agreg"
              title={
                <TabTitle>
                  Desm. por microrregião
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart6}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDpA />
              </TabContainer>
            </Tab>
            <Tab
              eventKey="desmatamento_anual_assoc"
              title={
                <TabTitle>
                  Desm. anual por associação / cooperativa
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart2}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDAA />
              </TabContainer>
            </Tab>
            <Tab
              eventKey="desmatamento_anual_medio"
              title={
                <TabTitle>
                  Desm. anual médio por família
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart3}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDAMF />
              </TabContainer>
            </Tab>
            <Tab
              eventKey="desmatamento_municipio"
              title={
                <TabTitle>
                  Desm. por município
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart4}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDM />
              </TabContainer>
            </Tab>
            <Tab
              eventKey="desmatamento_catfunda"
              title={
                <TabTitle>
                  Desm. por categoria fundiária
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={popoverChart5}
                  >
                    <ButtonInTab variant="light">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </ButtonInTab>
                  </OverlayTrigger>
                </TabTitle>
              }
            >
              <TabContainer>
                <DashDCF />
              </TabContainer>
            </Tab>
          </Tabs>
        </Card.Body>
      </TopMargedCard>

      <TopMargedCard>
        <Card.Body>
          <TituloCartao>
            <Card.Title>Associações e cooperativas</Card.Title>
            <span>clique no título de uma coluna para ordenar</span>
          </TituloCartao>
        <StyledTable striped>
          <thead>
            <tr>
              <StyledTH
                style={{ width: '25%' }}
                onClick={() => handleChangeOrder('assoc')}
              >
                Associação/Cooperativa{' '}
                <OrderMarker
                  field="assoc"
                  order={order}
                  direction={direction}
                />
              </StyledTH>
              <StyledTH
                style={{ width: '10%' }}
                onClick={() => handleChangeOrder('num_fornecedores')}
              >
                Num. Fornec.{' '}
                <OrderMarker
                  field="num_fornecedores"
                  order={order}
                  direction={direction}
                />
              </StyledTH>
              <StyledTH
                style={{ width: '10%' }}
                onClick={() => handleChangeOrder('risco_alto')}
              >
                Risco ALTO{' '}
                <OrderMarker
                  field="risco_alto"
                  order={order}
                  direction={direction}
                />
              </StyledTH>
              <StyledTH
                style={{ width: '10%' }}
                onClick={() => handleChangeOrder('risco_baixo')}
              >
                Risco BAIXO{' '}
                <OrderMarker
                  field="risco_baixo"
                  order={order}
                  direction={direction}
                />
              </StyledTH>
              <StyledTH
                style={{ width: '20%' }}
                onClick={() => handleChangeOrder('desm_last_three_years')}
              >
                Desmat. 3 anos{' '}
                <OrderMarker field="desm_last_three_years" order={order} direction={direction} />
              </StyledTH>
              <StyledTH
                style={{ width: '20%' }}
                onClick={() => handleChangeOrder('desm_accumulated')}
              >
                Desmat. acum.{' '}
                <OrderMarker field="desm_accumulated" order={order} direction={direction} />
              </StyledTH>
              <StyledTH
                style={{ width: '20%' }}
                onClick={() => handleChangeOrder('')}
              >
                % Desmat.{' '}
                <OrderMarker field="" order={order} direction={direction} />
              </StyledTH>
            </tr>
          </thead>
        </StyledTable>
        {mainTableData.length !== 0 && (
          <ScrolledData>
            <Table striped>
              <tbody>
                {mainTableData &&
                  mainTableData.map(
                    ({
                      assocId,
                      assoc_nome,
                      n_prods,
                      risco_alto_prods,
                      risco_baixo_prods,
                      percent_desm_accum,
                      desm_last_three_years,
                      desm_accumulated,
                    }) => (
                      <tr key={assocId}>
                        <td style={{ width: '25%' }}><strong>{assoc_nome}</strong></td>
                        <td style={{ width: '10%' }}>{n_prods}</td>
                        <td style={{ width: '10%' }}>
                          <SeloRisco
                            className={
                              (risco_alto_prods / n_prods) * 100 >= PERCENTUAL_RISCO_FORTE
                                ? 'forte'
                                : ''
                            }
                            title={`${Math.round((risco_alto_prods / n_prods) * 100)}% das famílias`}
                          >
                            {risco_alto_prods}
                          </SeloRisco>
                        </td>
                        <td style={{ width: '10%' }}>
                          {risco_baixo_prods}
                        </td>
                        <td style={{ width: '20%' }}>
                          <BarraNaCelula>
                            <span aria-hidden="true">
                              <span
                                style={{
                                  width: `${Math.max(
                                    1,
                                    Math.round(
                                      ((parseFloat(desm_last_three_years) || 0) /
                                        maiorDesmTresAnos) *
                                        100,
                                    ),
                                  )}%`,
                                }}
                              />
                            </span>
                            {numeral(parseFloat(desm_last_three_years)).format(
                              '0,0.00',
                            )}
                          </BarraNaCelula>
                        </td>
                        <td style={{ width: '20%' }}>
                          {numeral(parseFloat(desm_accumulated)).format(
                            '0,0.00',
                          )}
                        </td>
                        <td style={{ width: '20%' }}>
                          {numeral(parseFloat(percent_desm_accum)).format(
                            '0,0.00',
                          )}
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </Table>
          </ScrolledData>
        )}
        {mainTableData.length === 0 && <Loading>
          <Spinner animation="border" role="status">
            <span className="sr-only">Carregando...</span>
          </Spinner>
        </Loading>}
        </Card.Body>
      </TopMargedCard>

      <TopMargedCard>
        <Card.Body>
          <Card.Title>Associações fornecedoras da VEJA</Card.Title>
          <DashRank />
        </Card.Body>
      </TopMargedCard>
    </StyledContainer>
  );
}

function DashRank() {
  const [currentPage, setCurrentPage] = useState(
    sessionStorage.getItem('currentRankPage')
      ? parseInt(sessionStorage.getItem('currentRankPage'))
      : 1,
  );
  const [data, setData] = useState(null);
  const [paginatorData, setPaginatorData] = useState(null);

  const [order, setOrder] = useState(
    sessionStorage.getItem('currentOrder')
      ? sessionStorage.getItem('currentOrder')
      : '',
  );
  const [direction, setDirection] = useState(
    sessionStorage.getItem('currentDirection')
      ? sessionStorage.getItem('currentDirection')
      : 'asc',
  );

  const [filter, setFilter] = useState(
    sessionStorage.getItem('currentFilter')
      ? sessionStorage.getItem('currentFilter')
      : '',
  );
  const [filterText, setFilterText] = useState(
    sessionStorage.getItem('currentFilter')
      ? sessionStorage.getItem('currentFilter')
      : '',
  );

  const [showProducers, setShowProducers] = useState(null);
  const [producers, setProducers] = useState([]);

  let history = useHistory();

  useEffect(() => {
    async function fetchData() {
      let filterQuery = '';
      if (filter.length) filterQuery = `&filter=${filter}`;

      const {
        data: { rows, pages, hasPrevious, hasNext },
      } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/rank?page=${currentPage}&order=${order}&direction=${direction}${filterQuery}`,
      );
      setData(rows);

      setPaginatorData({ pages, hasPrevious, hasNext });
    }

    fetchData();
  }, [currentPage, order, direction, filter]);

  useEffect(() => {
    async function fetchProducers(gid) {
      setProducers(null);
      const {
        data: { rows },
      } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/areas/producer/${gid}?page=-1`,
      );
      setProducers(rows);
    }

    if (!showProducers) setProducers([]);
    else {
      fetchProducers(showProducers);
    }
  }, [showProducers]);

  const handlePageChange = (page) => {
    // TODO: loading...
    sessionStorage.setItem('currentRankPage', page);
    setCurrentPage(page);
  };

  const flyToRegion = (gid) => {
    history.push({
      pathname: '/home',
      state: {
        flyToRegion: gid,
      },
    });
  };

  const handleChangeOrder = (field) => {
    let newDirection;

    if (field === order) {
      newDirection = direction === 'asc' ? 'desc' : 'asc';
      setDirection(newDirection);
    } else {
      newDirection = field === '' ? 'desc' : 'asc';
      setDirection(newDirection);
      setOrder(field);
    }

    sessionStorage.setItem('currentOrder', field);
    sessionStorage.setItem('currentDirection', newDirection);
    handlePageChange(1);
  };

  const handleFilter = (filter) => {
    setFilter(filter);
    /* console.log(filter); */
    handlePageChange(1);
    sessionStorage.setItem('currentFilter', filter);
  };

  const handleFilterDebounced = useRef(
    _.debounce(handleFilter, 1000, { leading: false, trailing: true }),
  );

  const handleFilterChange = (e) => {
    const { value } = e.target;

    setFilterText(value);
    handleFilterDebounced.current(value);
  };

  const handleClearFilter = () => {
    handleFilter('');
    setFilterText('');
  };

  const handleChangeList = (gid) => {
    setShowProducers(showProducers === gid ? null : gid);
  };

  const popoverFly = (
    <Popover id="popover-basic">
      <Popover.Content>
        Acessar as informações desta microrregião no mapa interativo.
      </Popover.Content>
    </Popover>
  );
  const popoverList = (
    <Popover id="popover-basic">
      <Popover.Content>
        Listar os fornecedores desta associação.
      </Popover.Content>
    </Popover>
  );

  return (
    <>
      <Row>
        <Col sm={6}>
          <Form.Group>
            {/* <Form.Label>Filtrar por associação / cooperativa</Form.Label> */}
            <InputGroup>
              <Form.Control
                name="nome"
                placeholder="Filtre por nome da associação / cooperativa"
                value={filterText}
                onChange={handleFilterChange}
              />
              <InputGroup.Append>
                <Button onClick={handleClearFilter} variant="outline-secondary">
                  <FontAwesomeIcon icon={faEraser} />
                </Button>
              </InputGroup.Append>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Table>
        <thead>
          <tr>

            <th>
              <StyledDiv onClick={() => handleChangeOrder('assoc')}>
                Associação / Cooperativa{' '}
                <OrderMarker
                  field="assoc"
                  order={order}
                  direction={direction}
                />
              </StyledDiv>
            </th>

            <th>
              <StyledDiv onClick={() => handleChangeOrder('nprod')}>
                Num. fornecedores{' '}
                <OrderMarker
                  field="nprod"
                  order={order}
                  direction={direction}
                />
              </StyledDiv>
            </th>

            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map(({ gid, n_prods, nome }, idx) => (
              <Fragment key={gid}>
                <StrippedTR
                  key={gid}
                  className={classnames({ even: idx % 2 === 0 })}
                >
                  <td>
                    <AssocFiltered name={nome} filter={filter} />
                  </td>
                  <td>{n_prods}</td>
                  <RankButtons>
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement="left"
                      overlay={popoverList}
                    >
                      <Button
                        variant="outline-dark"
                        onClick={() => handleChangeList(gid)}
                      >
                        <FontAwesomeIcon icon={faList} />
                      </Button>
                    </OverlayTrigger>
                  </RankButtons>
                </StrippedTR>

                {showProducers === gid && (
                  <TRList className={classnames({ even: idx % 2 === 0 })}>
                    <td colSpan={4}>
                      <Card>
                        {!producers && (
                          <div>Carregando dados de Fornecedores...</div>
                        )}
                        {producers && (
                          <>
                            <ProducersListLabel>
                              Lista de fornecedores da associação
                            </ProducersListLabel>
                            <StyledTable>
                              <thead>
                                <tr>
                                  <th style={{ width: '28%' }}>Nome</th>
                                  <th style={{ width: '20%' }}>Colocação</th>
                                  <th style={{ width: '20%' }}>Seringal</th>
                                  <th style={{ width: '16%' }}>Risco</th>
                                  <th style={{ width: '16%' }}>Microrregião</th>
                                </tr>
                              </thead>
                            </StyledTable>

                            <ScrolledData2>
                              <Table>
                                <tbody>
                                  {producers.map(
                                    ({
                                      gid,
                                      nome,
                                      colocacao,
                                      seringal,
                                      risco,
                                      microId,
                                      micro_nome,
                                    }) => (
                                      <tr key={gid}>
                                        <td style={{ width: '28%' }}>{nome}</td>
                                        <td style={{ width: '20%' }}>
                                          {colocacao}
                                        </td>
                                        <td style={{ width: '20%' }}>
                                          {seringal}
                                        </td>
                                        <td style={{ width: '16%' }}>
                                          {getRiscoEscala(risco)}
                                        </td>
                                        <td style={{ width: '16%' }}>
                                          <div style={{ display: 'flex', gap: '5px' }}>
                                            <div>{micro_nome}</div>
                                            <div>
                                              <OverlayTrigger
                                                trigger={['hover', 'focus']}
                                                placement="left"
                                                overlay={popoverFly}
                                              >
                                                <Button
                                                  variant="outline-dark"
                                                  onClick={() => flyToRegion(microId)}
                                                  size='sm'
                                                >
                                                  <FontAwesomeIcon icon={faLocationArrow} />
                                                </Button>
                                              </OverlayTrigger>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </Table>
                            </ScrolledData2>
                          </>
                        )}
                      </Card>
                    </td>
                  </TRList>
                )}
              </Fragment>
            ))}

          {data && data.length === 0 && (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center' }}>
                Nenhum item encontrado para este filtro!
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <PaginatorContainer>
        <Paginator
          {...paginatorData}
          currentPage={currentPage}
          onChangePage={handlePageChange}
        />
      </PaginatorContainer>
    </>
  );
}

function OrderMarker({ field, order, direction }) {
  return (
    <>
      {field === order && (
        <>
          {direction === 'asc' && (
            <OMarker>
              <FontAwesomeIcon icon={faArrowDown} />
            </OMarker>
          )}
          {direction === 'desc' && (
            <OMarker>
              <FontAwesomeIcon icon={faArrowUp} />
            </OMarker>
          )}
        </>
      )}
    </>
  );
}

function AssocFiltered({ name, filter }) {
  let lfilter = filter.toLowerCase();
  const lname = name.toLowerCase();

  let startText = name.slice(0, lname.indexOf(lfilter));
  const endText = name.slice(lname.indexOf(lfilter) + filter.length);

  // capitalize
  if (startText.length)
    startText = startText.charAt(0).toUpperCase() + startText.slice(1);
  else lfilter = lfilter.charAt(0).toUpperCase() + lfilter.slice(1);

  if (filter.length)
    return (
      <>
        {startText}
        <strong>{lfilter}</strong>
        {endText}
      </>
    );
  else return <>{name}</>;
}

const StyledDiv = styled.div`
  cursor: pointer;
`;

const OMarker = styled.span`
  font-size: 0.85em;
  color: #666;
`;

// Visual comum dos gráficos (o nivo não lê as variáveis CSS do tema)
const TEMA_GRAFICO = {
  fontFamily: 'Inter, Helvetica, Arial, sans-serif',
  fontSize: 12,
  textColor: '#5c5f5a',
  axis: {
    domain: { line: { stroke: '#c9ccc6', strokeWidth: 1 } },
    ticks: { line: { stroke: '#c9ccc6', strokeWidth: 1 }, text: { fill: '#5c5f5a' } },
    legend: { text: { fill: '#3d403b', fontWeight: 600 } },
  },
  grid: { line: { stroke: '#eef0ec', strokeWidth: 1 } },
  legends: { text: { fill: '#3d403b' } },
  tooltip: {
    container: {
      fontSize: 13,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    },
  },
};

const formatarHa = (valor) => numeral(valor).format('0,0.0');

const eixoHectares = (legenda = 'desmatamento (hectares)', offset = -60) => ({
  tickSize: 0,
  tickPadding: 8,
  format: (valor) => numeral(valor).format('0,0'),
  legend: legenda,
  legendPosition: 'middle',
  legendOffset: offset,
});

const EIXO_ANOS = { tickSize: 0, tickPadding: 8 };

const LEGENDA_LATERAL = {
  dataFrom: 'keys',
  anchor: 'right',
  direction: 'column',
  justify: false,
  translateX: 140,
  translateY: 0,
  itemsSpacing: 4,
  itemWidth: 130,
  itemHeight: 18,
  itemDirection: 'left-to-right',
  symbolSize: 12,
  symbolShape: 'square',
  effects: [{ on: 'hover', style: { itemTextColor: '#1a1a1a' } }],
};

const ehAnoCorrente = (ano) => String(ano) === String(moment().year());

// Cor da série de um ano só: o ano corrente (ainda parcial) fica claro
const corPorAno = ({ indexValue }) =>
  ehAnoCorrente(indexValue) ? COR_ANO_PARCIAL : COR_DESMATAMENTO;

// Clareia uma cor #rrggbb misturando com branco, para o ano parcial nas séries empilhadas
function clarear(cor, quanto = 0.6) {
  const hex = /^#([0-9a-f]{6})$/i.exec(cor || '');
  if (!hex) return cor;

  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex[1].substr(i, 2), 16));
  const mistura = (canal) => Math.round(canal + (255 - canal) * quanto);
  return `rgb(${mistura(r)}, ${mistura(g)}, ${mistura(b)})`;
}

function TooltipGrafico({ titulo, serie, value }) {
  return (
    <span>
      <strong>{titulo}</strong>
      {serie ? ` · ${serie}` : ''}: {formatarHa(value)} ha
      {ehAnoCorrente(titulo) ? ' (parcial)' : ''}
    </span>
  );
}

function LegendaAnoParcial({ ultimoDeter, comFechado = true }) {
  return (
    <LegendaGrafico>
      {comFechado && <span><i className="fechado" />Ano fechado (PRODES)</span>}
      <span>
        <i className="parcial" />
        {moment().year()} parcial: alertas DETER
        {ultimoDeter ? ` até ${moment(ultimoDeter).format('DD/MM/YYYY')}` : ''}
      </span>
    </LegendaGrafico>
  );
}

function BotaoImprimir({ conteudo }) {
  return (
    <ReactToPrint
      className="print-button"
      trigger={() => (
        <PrintButton variant="outline-dark" aria-label="Imprimir gráfico">
          <FontAwesomeIcon icon={faPrint} /> Imprimir
        </PrintButton>
      )}
      content={() => conteudo.current}
    />
  );
}

function CarregandoGrafico() {
  return (
    <Loading>
      <Spinner className="graph" animation="border" role="status">
        <span className="sr-only">Carregando...</span>
      </Spinner>
    </Loading>
  );
}

function DashDAT({ totais, ultimoDeter }) {
  const componentRef = useRef();

  const values = totais
    ? totais.map(({ ano, ha }) => ({ ano: String(ano), desmatamento: Math.round(ha * 100) / 100 }))
    : null;

  return (
    <>
      <ChartContainer ref={componentRef}>
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento anual total</strong>
        </PrintLabel>

        {!values && <CarregandoGrafico />}

        {values && (
          <ResponsiveBar
            className="chart"
            data={values}
            keys={['desmatamento']}
            indexBy="ano"
            margin={{ top: 20, right: 20, bottom: 40, left: 80 }}
            padding={0.3}
            colors={corPorAno}
            borderRadius={2}
            theme={TEMA_GRAFICO}
            axisTop={null}
            axisRight={null}
            axisBottom={EIXO_ANOS}
            axisLeft={eixoHectares()}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={({ indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        <LegendaAnoParcial ultimoDeter={ultimoDeter} />
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

function DashDpA() {
  const anoCorrente = moment().year();
  const anos = [];
  for (let ano = anoCorrente; ano >= 2009; ano--) anos.push(ano);

  const [data, setData] = useState(null);
  const [currentYear, setCurrentYear] = useState(anoCorrente - 1);

  const componentRef = useRef();

  useEffect(() => {
    async function fetchData() {
      setData(null);
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=per_assoc,${currentYear}`,
      );
      setData(prepareDataDashDpA(data));
    }

    fetchData();
  }, [currentYear]);

  // Barras horizontais: o nivo desenha a primeira embaixo, então a maior vai por último
  const values = data
    ? [...data.values].sort((a, b) => {
        const total = (linha) => data.keys.reduce((soma, key) => soma + (linha[key] || 0), 0);
        return total(a) - total(b);
      })
    : null;

  return (
    <>
      <Filtro>
        <Form.Label htmlFor="filtro-ano-microrregiao">Ano</Form.Label>
        <Form.Control
          id="filtro-ano-microrregiao"
          as="select"
          custom
          value={currentYear}
          onChange={(e) => setCurrentYear(parseInt(e.target.value))}
        >
          {anos.map((ano) => (
            <option value={ano} key={ano}>
              {ano === anoCorrente ? `${ano} (parcial)` : ano}
            </option>
          ))}
        </Form.Control>
      </Filtro>

      <ChartContainer ref={componentRef} className="alto">
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento por microrregião</strong> ({currentYear})
        </PrintLabel>

        {!values && <CarregandoGrafico />}

        {values && (
          <ResponsiveBar
            className="chart"
            data={values}
            keys={data.keys}
            indexBy="associacao"
            layout="horizontal"
            margin={{ top: 10, right: 290, bottom: 50, left: 160 }}
            padding={0.25}
            groupMode="stacked"
            colors={data.colorOf}
            theme={TEMA_GRAFICO}
            enableGridX={true}
            enableGridY={false}
            axisTop={null}
            axisRight={null}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            axisBottom={eixoHectares('desmatamento (hectares)', 40)}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            legends={[LEGENDA_LATERAL]}
            tooltip={({ id, indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} serie={id} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        {currentYear === anoCorrente ? <LegendaAnoParcial comFechado={false} /> : <span />}
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

function DashDAMF() {
  const [data, setData] = useState(null);

  const componentRef = useRef();

  useEffect(() => {
    async function fetchData() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=medio_familia`,
      );
      setData(prepareDataDAT(data));
    }

    fetchData();
  }, []);

  return (
    <>
      <ChartContainer ref={componentRef}>
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento anual m&eacute;dio por família</strong>
        </PrintLabel>

        {!data && <CarregandoGrafico />}

        {data && (
          <ResponsiveBar
            className="chart"
            data={data.values}
            keys={data.keys}
            indexBy="ano"
            margin={{ top: 20, right: 170, bottom: 40, left: 80 }}
            padding={0.3}
            groupMode="stacked"
            colors={(bar) =>
              ehAnoCorrente(bar.indexValue) ? clarear(data.colorOf(bar)) : data.colorOf(bar)
            }
            theme={TEMA_GRAFICO}
            axisTop={null}
            axisRight={null}
            axisBottom={EIXO_ANOS}
            axisLeft={eixoHectares('desmatamento (hectares / família)')}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            legends={[LEGENDA_LATERAL]}
            tooltip={({ id, indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} serie={id} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        <Nota>O ano {moment().year()} aparece em tons claros: é parcial, só com alertas DETER.</Nota>
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

function DashDAA() {
  const [data, setData] = useState(null);

  const [assocs, setAssocs] = useState([]);
  const [currentAssoc, setCurrentAssoc] = useState(null);

  const componentRef = useRef();

  useEffect(() => {
    async function fetchDetails() {
      const { data: assocs } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/manage/assocs`,
      );
      setAssocs(assocs);
      setCurrentAssoc(assocs[0].id);
    }
    fetchDetails();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=associacao,${currentAssoc}`,
      );
      setData(data);
    }

    if (currentAssoc) fetchData();
  }, [currentAssoc]);

  const assocById = assocs.filter(
    (assoc) => String(assoc.id) === String(currentAssoc),
  );

  return (
    <>
      <Filtro>
        <Form.Label htmlFor="filtro-associacao">Associação / cooperativa</Form.Label>
        {currentAssoc && (
          <Form.Control
            id="filtro-associacao"
            as="select"
            custom
            value={currentAssoc}
            onChange={(e) => setCurrentAssoc(e.target.value)}
          >
            {assocs.map(({ id, nome }) => (
              <option value={id} key={id}>
                {nome}
              </option>
            ))}
          </Form.Control>
        )}
      </Filtro>

      <ChartContainer ref={componentRef}>
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento anual por associação / cooperativa</strong> ({assocById[0]?.nome})
        </PrintLabel>

        {!data && <CarregandoGrafico />}

        {data && (
          <ResponsiveBar
            className="chart"
            data={prepareDataDA(data)}
            keys={['desmatamento']}
            indexBy="ano"
            margin={{ top: 20, right: 20, bottom: 40, left: 80 }}
            padding={0.3}
            colors={corPorAno}
            borderRadius={2}
            theme={TEMA_GRAFICO}
            axisTop={null}
            axisRight={null}
            axisBottom={EIXO_ANOS}
            axisLeft={eixoHectares()}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={({ indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        <LegendaAnoParcial />
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

function DashDM() {
  const [data, setData] = useState(null);

  const [muns, setMuns] = useState([]);
  const [currentMun, setCurrentMun] = useState(null);

  const componentRef = useRef();

  useEffect(() => {
    async function fetchDetails() {
      const { data: muns } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/manage/municipios`,
      );
      setMuns(muns);
      setCurrentMun(muns[0].cd_mun);
    }
    fetchDetails();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=municipios,${currentMun}`,
      );
      setData(data);
    }

    if (currentMun) fetchData();
  }, [currentMun]);

  const getMunName = () => {
    const found = muns.find(m => m.cd_mun === currentMun);
    return found ? found.nm_mun : '';
  }

  return (
    <>
      <Filtro>
        <Form.Label htmlFor="filtro-municipio">Município</Form.Label>
        {currentMun && (
          <Form.Control
            id="filtro-municipio"
            as="select"
            custom
            value={currentMun}
            onChange={(e) => setCurrentMun(e.target.value)}
          >
            {muns.map(({ cd_mun: id, nm_mun: nome }) => (
              <option value={id} key={id}>
                {nome}
              </option>
            ))}
          </Form.Control>
        )}
      </Filtro>

      <ChartContainer ref={componentRef}>
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento por município</strong> ({getMunName()})
        </PrintLabel>

        {!data && <CarregandoGrafico />}

        {data && (
          <ResponsiveBar
            className="chart"
            data={prepareDataDA(data)}
            keys={['desmatamento']}
            indexBy="ano"
            margin={{ top: 20, right: 20, bottom: 40, left: 80 }}
            padding={0.3}
            colors={corPorAno}
            borderRadius={2}
            theme={TEMA_GRAFICO}
            axisTop={null}
            axisRight={null}
            axisBottom={EIXO_ANOS}
            axisLeft={eixoHectares()}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={({ indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        <LegendaAnoParcial />
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

function DashDCF() {
  const [data, setData] = useState(null);

  const [cats, setCats] = useState([]);
  const [currentCat, setCurrentCat] = useState(null);

  const componentRef = useRef();

  useEffect(() => {
    async function fetchDetails() {
      const { data: cats } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/manage/catfundas`,
      );
      setCats(cats);
      setCurrentCat(cats[0].gid);
    }
    fetchDetails();
  }, []);

  useEffect(() => {
    async function fetchData() {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER}geo-service/dash/desm/?options=catfundas,${currentCat}`,
      );
      setData(data);
    }

    if (currentCat) fetchData();
  }, [currentCat]);

  const getCatFundName = () => {
    const found = cats.find(c => String(c.gid) === String(currentCat));
    return found ? found.nome : '';
  }

  return (
    <>
      <Filtro>
        <Form.Label htmlFor="filtro-categoria">Categoria fundiária</Form.Label>
        {currentCat && (
          <Form.Control
            id="filtro-categoria"
            as="select"
            custom
            value={currentCat}
            onChange={(e) => setCurrentCat(e.target.value)}
          >
            {cats.map(({ gid, nome }) => (
              <option value={gid} key={gid}>
                {nome}
              </option>
            ))}
          </Form.Control>
        )}
      </Filtro>

      <ChartContainer ref={componentRef}>
        <style type="text/css" media="print">{"\
          @page { size: 370mm 160mm;  }\
        "}</style>

        <PrintLabel className="only-print">
          <strong>Desmatamento por categoria fundiária</strong> ({getCatFundName()})
        </PrintLabel>

        {!data && <CarregandoGrafico />}

        {data && (
          <ResponsiveBar
            className="chart"
            data={prepareDataDA(data)}
            keys={['desmatamento']}
            indexBy="ano"
            margin={{ top: 20, right: 20, bottom: 40, left: 80 }}
            padding={0.3}
            colors={corPorAno}
            borderRadius={2}
            theme={TEMA_GRAFICO}
            axisTop={null}
            axisRight={null}
            axisBottom={EIXO_ANOS}
            axisLeft={eixoHectares()}
            enableLabel={false}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={({ indexValue, value }) => (
              <TooltipGrafico titulo={indexValue} value={value} />
            )}
          />
        )}
      </ChartContainer>

      <RodapeGrafico>
        <LegendaAnoParcial />
        <BotaoImprimir conteudo={componentRef} />
      </RodapeGrafico>
    </>
  );
}

/* Styles */

const StyledContainer = styled(Container)`
  margin: 20px auto;
  max-width: 90vw;

  @media (min-width: 1300px) {
    max-width: 1450px;
  }

  font-family: var(--vft-font);
  & .card-title {
    margin: 0;
    font-weight: 700;
    color: var(--vft-ink);
    font-size: 18px;
  }
  & .card-text {
    line-height: 18px;
    font-size: 16px;
    text-align: justify;
    margin-bottom: 10px;
  }
`;

const Cabecalho = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    line-height: 1.2;
    font-weight: 700;
  }
`;

const Sobretitulo = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vft-muted);
`;

const Acoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .btn {
    min-height: 44px;
    padding: 0 18px;
  }
  .btn svg {
    margin-right: 6px;
  }
`;

const Contagens = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 0 4px;
  font-size: 14px;
  color: var(--vft-text);

  span[tabindex] {
    cursor: help;
    border-bottom: 1px dotted var(--vft-border-strong);
  }
`;

const TituloCartao = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;

  span {
    font-size: 13px;
    color: var(--vft-muted);
  }
`;

const SeloRisco = styled.span`
  display: inline-block;
  min-width: 44px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--vft-accent-soft);
  color: var(--vft-accent);
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;

  &.forte {
    background: var(--vft-accent);
    color: #fff;
  }
`;

const BarraNaCelula = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-variant-numeric: tabular-nums;

  & > span {
    flex: 0 0 90px;
    height: 8px;
    border-radius: 4px;
    background: var(--vft-hover);
    overflow: hidden;
  }
  & > span > span {
    display: block;
    height: 100%;
    background: var(--vft-accent);
  }
`;

const LegendaGrafico = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 12.5px;
  color: var(--vft-text);

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  i {
    width: 14px;
    height: 14px;
    border-radius: 3px;
  }
  i.fechado {
    background: var(--vft-accent);
  }
  i.parcial {
    background: var(--vft-accent-partial);
  }
`;

const TopMargedCard = styled(Card)`
  margin: 20px 0;
  font-family: var(--vft-font);
`;

const TabContainer = styled.div`
  margin: 20px;
`;

const StyledPopover = styled(Popover)`
  max-width: 800px;
  & .popover-header {
    background-color: #000;
    color: #fff;
  }
`;

const ChartContainer = styled.div`
  margin-top: 10px;
  height: 460px;

  &.alto {
    height: 560px;
  }
  & .chart {
    height: 100%;
  }
`;

const Filtro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 360px;

  label {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--vft-muted);
  }
`;

const RodapeGrafico = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;

  & > div,
  & > span {
    margin-top: 0;
  }
`;

const Nota = styled.span`
  font-size: 12.5px;
  color: var(--vft-muted);
`;

const PaginatorContainer = styled.div`
  width: 100%;
  text-align: right;
`;

const ButtonInTab = styled(Button)`
  margin-left: 6px;
  padding: 0 2px;
  border: 0 !important;
  background: transparent !important;
  color: inherit !important;
  line-height: 1;
`;

const StyledTD = styled.td`
  text-align: center;
`;

const TabTitle = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ScrolledData = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

const StyledTable = styled(Table)`
  margin-bottom: 0;
  th {
    font-size: 0.9em;
  }
`;

const StyledTH = styled.th`
  cursor: pointer;
`;

const RankButtons = styled.td`
  button {
    margin-left: 10px;
  }
`;

const TRList = styled.tr`
  &.even {
    background-color: rgba(0, 0, 0, 0.05);
  }
  & > td {
    padding: 0;
  }
  & > td > div {
    margin: 10px;
  }
`;

const ScrolledData2 = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const StrippedTR = styled.tr`
  &.even {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const ProducersListLabel = styled.h4`
  padding: 5px 0 0 10px;
`;

const Loading = styled.div`
  margin-top: 20px;

  & .graph {
    margin-left: 20px;
  }
`;

const PrintButton = styled(Button)`
  border-color: var(--vft-border-strong);
`;

const PrintLabel = styled.div`
  margin-left: 20px;
`;

export default Dashboard;
